import {
  closestCenter,
  DndContext,
  type DragEndEvent,
  type DragOverEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  type ProfileModuleDto,
  ProfileModuleType,
  type ProfileTemplateFieldMode,
} from '@up2stay/up2promo-api-client';
import { ConfirmDialog, formatPhoneForDisplay } from '@up2stay/up2promo-ui';
import { Input } from '@up2stay/up2promo-ui';
import { InlineEditCard } from '@up2stay/up2promo-ui';
import { Label } from '@up2stay/up2promo-ui';
import { Button } from '@up2stay/up2promo-ui';
import { isValidPhoneNumber, PhoneInput } from '@up2stay/up2promo-ui';
import { cn } from '@up2stay/up2promo-ui';
import { GripVertical, Mail, Phone, Plus, Trash2 } from 'lucide-react';
import { useMemo, useRef, useState } from 'react';
import de from 'react-phone-number-input/locale/de';
import en from 'react-phone-number-input/locale/en';
import type {
  ModuleEntryGovernanceProps,
  TemplateOverrideLabels,
} from '@/core/components/profile/module-entry-governance';
import {
  ModuleEntryListRowBody,
  ModuleEntryLockedHint,
  ModuleEntryOverrideHint,
  ModuleEntryPolicySlot,
} from '@/core/components/profile/module-entry-governance-ui';
import { useDndProfileSensors } from '@/core/hooks/use-dnd-profile-sensors';
import { useLocale } from '@/core/hooks/use-locale';
import { useIsMobile } from '@/core/hooks/use-mobile';
import {
  lockBodyScrollDuringDrag,
  triggerDragEndHaptic,
  triggerDragOverHaptic,
  triggerDragStartHaptic,
  unlockBodyScrollAfterDrag,
} from '@/core/lib/dnd-haptics';
import {
  isEntryPolicyLocked,
  resolveEntryPolicy,
} from '@/core/lib/profile-module-entry-policies';
import type { MemberRosterRow } from '@/core/lib/profile-template-member-values-csv';
import { useDictionary } from '@/core/providers/dictionary-provider';
import type {
  ContactInfoEntry,
  ContactInfoModuleConfig,
  ContactType,
} from '@/core/types/profile-modules';
import { ContactEditForm } from '@/core/components/profile/modules/contact-info-edit-form';

interface SortableContactItemProps {
  entry: ContactInfoEntry;
  itemIndex: number;
  onEdit: () => void;
  onRemove: () => void;
  structureReadOnly?: boolean;
  entryLocked?: boolean;
  showEntryPolicy?: boolean;
  entryPolicy?: ProfileTemplateFieldMode;
  onEntryPolicyChange?: (mode: ProfileTemplateFieldMode) => void;
  policyLabels?: { locked: string; free: string };
  fieldLockedHintMessage?: string;
  showEntryOverrideHints?: boolean;
  currentModule?: ProfileModuleDto;
  templateModule?: ProfileModuleDto;
  entryId?: string;
  overrideHintMessage?: string;
  templateOverrideLabels?: TemplateOverrideLabels;
  onResetEntry?: () => void;
  workspaceId?: string;
  templateId?: string;
  publishStatus?: ModuleEntryGovernanceProps['publishStatus'];
  memberRoster?: MemberRosterRow[];
  memberRosterLoading?: boolean;
  hasAssignedTeams?: boolean;
  memberValuesDict?: ModuleEntryGovernanceProps['memberValuesDict'];
  canManageMemberValues?: boolean;
}

export function SortableContactEditSlot({
  itemIndex,
  children,
}: {
  itemIndex: number;
  children: React.ReactNode;
}) {
  const id = `item-${itemIndex}`;
  const { setNodeRef, transform, transition, isDragging } = useSortable({
    id,
    disabled: true,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition: isDragging ? transition : 'none',
  };

  return (
    <div ref={setNodeRef} style={style}>
      {children}
    </div>
  );
}

export function SortableContactItem({
  entry,
  itemIndex,
  onEdit,
  onRemove,
  structureReadOnly = false,
  entryLocked = false,
  showEntryPolicy = false,
  entryPolicy,
  onEntryPolicyChange,
  policyLabels,
  fieldLockedHintMessage,
  showEntryOverrideHints = false,
  currentModule,
  templateModule,
  entryId: entryIdProp,
  overrideHintMessage,
  templateOverrideLabels,
  onResetEntry,
  workspaceId,
  templateId,
  publishStatus,

  memberRoster,
  memberRosterLoading,
  hasAssignedTeams,
  memberValuesDict,
  canManageMemberValues = true,
}: SortableContactItemProps) {
  const id = `item-${itemIndex}`;
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id, disabled: structureReadOnly });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition: isDragging ? transition : 'none',
  };

  const Icon = entry.kind === 'email' ? Mail : Phone;
  // Show phones like the edit field ("+49 30 000000"), not raw E.164.
  const displayValue =
    entry.kind === 'email' ? entry.value : formatPhoneForDisplay(entry.value);
  const primary = entry.label?.trim() || displayValue;
  const secondary = entry.label?.trim() ? displayValue : null;

  const isMobile = useIsMobile();
  const policySlot = (
    <ModuleEntryPolicySlot
      showEntryPolicy={showEntryPolicy}
      entryPolicy={entryPolicy}
      onEntryPolicyChange={onEntryPolicyChange}
      policyLabels={policyLabels}
      entryId={entryIdProp}
      entryLabel={primary || (entry.kind === 'email' ? 'E-Mail' : 'Telefon')}
      memberValueColumn={
        entryIdProp
          ? {
              kind: 'moduleEntry',
              entryId: entryIdProp,
              moduleId: templateModule?.id ?? currentModule?.id,
              label: primary || (entry.kind === 'email' ? 'E-Mail' : 'Telefon'),
              inputType: entry.kind === 'phone' ? 'phone' : 'email',
            }
          : undefined
      }
      memberDialogTitle={
        primary || (entry.kind === 'email' ? 'E-Mail' : 'Telefon')
      }
      memberDefaultValue={entry.value ?? ''}
      workspaceId={workspaceId}
      templateId={templateId}
      publishStatus={publishStatus}
      memberRoster={memberRoster}
      memberRosterLoading={memberRosterLoading}
      hasAssignedTeams={hasAssignedTeams}
      memberValuesDict={memberValuesDict}
      canManage={canManageMemberValues}
    />
  );

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={cn(
        'rounded-lg border',
        isDragging &&
          'opacity-80 shadow-lg z-10 bg-background ring-2 ring-primary',
        isMobile && 'touch-pan-y cursor-grab active:cursor-grabbing',
      )}
      {...(isMobile ? { ...listeners, ...attributes } : {})}
    >
      <ModuleEntryListRowBody
        isMobile={isMobile}
        structureReadOnly={structureReadOnly}
        dragHandle={
          <div
            data-dnd-grip
            {...listeners}
            {...attributes}
            className={cn(
              'touch-none shrink-0 rounded p-0.5 cursor-grab active:cursor-grabbing',
            )}
            aria-label="Reihenfolge ändern"
          >
            <GripVertical className="h-4 w-4 text-muted-foreground" />
          </div>
        }
        main={
          <button
            type="button"
            onClick={onEdit}
            disabled={entryLocked && !showEntryPolicy}
            className="flex w-full min-w-0 cursor-pointer items-center gap-3 text-left rounded-md p-2 -m-2 disabled:cursor-default disabled:opacity-70"
          >
            <Icon className="h-5 w-5 shrink-0 text-muted-foreground" />
            <div className="min-w-0 flex-1">
              {/* Wrap instead of truncating: the policy select leaves the
                  text ~110px in templates (DEV-538). */}
              <div className="font-medium [overflow-wrap:anywhere]">
                {primary || (entry.kind === 'email' ? 'E-Mail' : 'Telefon')}
              </div>
              {secondary ? (
                <div className="text-sm text-muted-foreground [overflow-wrap:anywhere]">
                  {secondary}
                </div>
              ) : null}
              <ModuleEntryLockedHint
                entryLocked={entryLocked}
                fieldLockedHintMessage={fieldLockedHintMessage}
                className="mt-1"
              />
            </div>
          </button>
        }
        policy={policySlot}
        trailing={
          !structureReadOnly ? (
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="shrink-0 opacity-70 hover:opacity-100"
              onClick={(e) => {
                e.stopPropagation();
                onRemove();
              }}
              aria-label="Entfernen"
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          ) : null
        }
      />
      {currentModule &&
      templateModule &&
      entryIdProp &&
      onResetEntry &&
      templateOverrideLabels ? (
        <ModuleEntryOverrideHint
          currentModule={currentModule}
          templateModule={templateModule}
          entryId={entryIdProp}
          showEntryOverrideHints={showEntryOverrideHints}
          overrideHintMessage={overrideHintMessage}
          templateOverrideLabels={templateOverrideLabels}
          onReset={onResetEntry}
          className="mt-1.5 px-3 pb-2"
        />
      ) : null}
    </div>
  );
}
